/**
 * StationView — Line cook station view for food service.
 *
 * Shows orders assigned to cook's station with SLA timers.
 * Follows DRR-055: Dumb Frontend — all data from backend.
 */

import { useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Loader,
  Alert,
  Progress,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconFlame,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconRefresh,
  IconChefHat,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { useMyActor } from '@/hooks/useMyActor';
import {
  useFoodServiceStationOrdersQuery,
  useFoodServiceMyStationAssignmentQuery,
  FoodServiceStation,
  FoodServiceOrderStatus,
  FoodServiceDishStatus,
} from '@/generated/graphql';

export function StationView() {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  const { actorId } = useMyActor();

  // Station assignment from backend (DRR-055: backend-driven)
  const { data: assignmentData, loading: assignmentLoading } = useFoodServiceMyStationAssignmentQuery({
    skip,
  });
  const station = assignmentData?.myStationAssignment?.station ?? FoodServiceStation.GRILL;

  // Query orders for this station using Food Service API
  const {
    data: ordersData,
    loading: ordersLoading,
    refetch: refetchOrders,
  } = useFoodServiceStationOrdersQuery({
    variables: { station },
    skip,
    pollInterval: 10000,
  });

  const orders = ordersData?.stationOrders ?? [];

  // Group orders by status
  const inProgressOrders = useMemo(
    () => orders.filter((o) => o.status === FoodServiceOrderStatus.IN_PREP),
    [orders],
  );
  const queuedOrders = useMemo(
    () => orders.filter((o) => o.status === FoodServiceOrderStatus.RECEIVED),
    [orders],
  );
  const completedOrders = useMemo(
    () => orders.filter((o) => o.status === FoodServiceOrderStatus.QUALITY_CHECK),
    [orders],
  );

  const handleRefresh = useCallback(() => {
    refetchOrders();
  }, [refetchOrders]);

  const handleStartOrder = useCallback((orderId: string) => {
    // TODO: Use fireEvent mutation to advance workflow
    console.log('Starting order:', orderId);
  }, []);

  const handleCompleteOrder = useCallback((orderId: string) => {
    // TODO: Use fireEvent mutation to advance to quality_check
    console.log('Completing order:', orderId);
  }, []);

  if (ordersLoading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center">
          <Loader size="lg" />
          <Text>Loading station...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="xl" color="orange" variant="light">
              <IconFlame size={28} />
            </ThemeIcon>
            <div>
              <Title order={2}>{station} Station</Title>
              <Text size="sm" c="dimmed">
                {inProgressOrders.length} cooking, {queuedOrders.length} queued
              </Text>
            </div>
          </Group>
          <Tooltip label="Refresh orders">
            <ActionIcon variant="light" size="lg" onClick={handleRefresh}>
              <IconRefresh size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Active Orders */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Now Cooking
          </Title>
          {inProgressOrders.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No active orders. Waiting for new tickets...
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {inProgressOrders.map((order) => (
                <Card key={order.id} withBorder shadow="sm" padding="md">
                  <Group justify="space-between" mb="xs">
                    <Badge color="blue" variant="light">
                      #{order.orderNumber}
                    </Badge>
                    <Badge color="orange" variant="filled">
                      COOKING
                    </Badge>
                  </Group>
                  <Text fw={500} size="lg" mb="xs">
                    {order.brand}
                  </Text>
                  <Stack gap="xs" mb="md">
                    {order.items.map((item) => (
                      <Group key={item.id} justify="space-between">
                        <Text size="sm">{item.quantity}x {item.name}</Text>
                        <Badge size="xs" color={item.status === FoodServiceDishStatus.COOKING ? 'orange' : 'gray'}>
                          {item.status}
                        </Badge>
                      </Group>
                    ))}
                  </Stack>
                  {order.specialInstructions && (
                    <Alert color="yellow" variant="light" mb="md">
                      {order.specialInstructions}
                    </Alert>
                  )}
                  <Progress
                    value={Math.min((order.elapsedSeconds / (order.slaTargetMinutes * 60)) * 100, 100)}
                    color={order.elapsedSeconds > order.slaTargetMinutes * 60 ? 'red' : 'blue'}
                    size="sm"
                    mb="xs"
                  />
                  <Button
                    fullWidth
                    color="green"
                    leftSection={<IconCheck size={16} />}
                    onClick={() => handleCompleteOrder(order.id)}
                  >
                    Done
                  </Button>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Paper>

        {/* Queued Orders */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Queue ({queuedOrders.length})
          </Title>
          {queuedOrders.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              Queue empty
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
              {queuedOrders.slice(0, 8).map((order) => (
                <Card key={order.id} withBorder padding="sm">
                  <Group justify="space-between" mb="xs">
                    <Text fw={500} size="sm" lineClamp={1}>
                      #{order.orderNumber} - {order.brand}
                    </Text>
                    <Badge size="xs" color="gray">
                      NEW
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed" mb="xs">
                    {order.items.length} items
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    fullWidth
                    onClick={() => handleStartOrder(order.id)}
                  >
                    Start
                  </Button>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Paper>

        {/* Completed Today */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>Sent to QC</Title>
            <Badge color="green" size="lg">
              {completedOrders.length}
            </Badge>
          </Group>
          <Progress
            value={(completedOrders.length / (completedOrders.length + queuedOrders.length + inProgressOrders.length || 1)) * 100}
            size="xl"
            color="green"
          />
        </Paper>
      </Stack>
    </Container>
  );
}

export default StationView;
